import { useSuspenseQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { createContext, use } from 'react'

import { appConfigQuery, isUpdateRequired, type AppConfig, type FeatureName } from '@/shared/api'
import { toPersianDigits } from '@/shared/lib'
import { native } from '@/shared/native'

/**
 * `GET /app/config` must resolve before the router renders (backend-routes.md
 * §21): it carries the kill switch, the force-update gate and the feature
 * flags that decide whether two whole flows exist.
 *
 * Force-update and kill-switch have **no design in Figma**. The two screens
 * below are deliberately minimal placeholders rather than invented UI — they
 * need a product decision before they can be drawn.
 */
const AppConfigContext = createContext<AppConfig | null>(null)

export function useAppConfig(): AppConfig {
  const config = use(AppConfigContext)
  if (!config) throw new Error('useAppConfig used outside AppConfigGate')
  return config
}

/** Feature flags from §22's unresolved domains — the client hides, never 404s. */
export function useFeature(name: FeatureName): boolean {
  return useAppConfig().features[name]
}

function Blocked({ title, body }: { title: string; body: string }) {
  return (
    <main className="sx-backdrop-app flex min-h-dvh flex-col items-center justify-center gap-3 px-8 text-center">
      <h1 className="text-24 font-bold text-ink">{title}</h1>
      <p className="text-15 text-ink-80">{body}</p>
    </main>
  )
}

export function AppConfigGate({ children }: { children: ReactNode }) {
  const { data: config } = useSuspenseQuery(appConfigQuery)

  if (config.kill_switch.enabled) {
    return (
      <Blocked
        title="سرویس موقتاً در دسترس نیست"
        body={config.kill_switch.reason_fa ?? 'لطفاً بعداً دوباره تلاش کنید.'}
      />
    )
  }

  if (isUpdateRequired(config, native.device.getClientVersion())) {
    return (
      <Blocked
        title="به‌روزرسانی لازم است"
        body={`برای ادامه، نسخه ${toPersianDigits(config.latest_version)} را نصب کنید.`}
      />
    )
  }

  return <AppConfigContext value={config}>{children}</AppConfigContext>
}
